import React, { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, ChevronRight, Calendar, Clock, CheckCircle2, X, BookOpen, Repeat, Trash2, Target } from 'lucide-react';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useGoals } from '../hooks/useGoals';
import { format, addDays, isSameDay, subDays } from 'date-fns';
import { GOAL_TYPE_CONFIG, goalTypes } from '../types/goals';
import { Link } from 'react-router-dom';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { db } from '../firebase';
import { useMultiGoalProgress } from '../hooks/useGoalProgress';
import { initializeActionLogsForGoal } from '../utils/goalUtils';
import { MobilePageTransition } from './Quran/components/MobilePageTransition';
import { SelectionView } from './Quran/components/SelectionView';
import { useGoalProgress } from '../hooks/useGoalProgress';

// Add helper copy for each goal type and option
const goalTypeDescriptions = {
  quran_reading: 'Set a goal to complete the Quran in a set number of days.',
  memorization: 'Track your progress as you memorize surahs or sections.',
  dhikr: 'Stay consistent with your daily remembrance and supplications.'
};
const goalOptionDescriptions = {
  read_15_days: 'A focused plan to complete the Quran in just over two weeks.',
  read_30_days: 'A steady pace to finish the Quran in a month.',
  read_60_days: 'A gentle plan to complete the Quran in two months.',
  memorize_short: 'Start with shorter surahs to build your memorization habit.',
  memorize_juz_amma: 'Memorize the 30th Juz (Amma) over a few months.',
  morning_adhkar: 'Morning remembrances to start your day with blessings.',
  evening_adhkar: 'Evening remembrances for peace and protection.',
  after_prayer: 'Dhikr to recite after each obligatory prayer.'
};

const GoalSelectionView = ({ onClose, isDarkMode, onGoalCreated, hideHeader = false }) => {
  const [selectedType, setSelectedType] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);

  const handleGoalCreate = async () => {
    if (!selectedType || !selectedOption) return;
    const option = selectedType.options.find(o => o.value === selectedOption.value);
    if (!option) return;
    try {
      await onGoalCreated(
        selectedType.id,
        option.label,
        option.recurrenceType,
        option.details
      );
    } catch (error) {
      console.error('Error creating goal:', error);
    }
  };

  // Main goal type selection view
  if (!selectedType) {
    return (
      <div className="flex flex-col h-full">
        {/* Only render header if hideHeader is true (for standalone use) */}
        {hideHeader && (
          <header className="sticky top-0 z-40">
            <div>
              <div style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' }} className="px-4 py-4">
                <div className="flex items-center justify-between">
                  <button
                    onClick={onClose}
                    className={`flex items-center ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}
                  >
                    <ChevronRight className="w-5 h-5 rotate-180" />
                    <span className="text-xl">Cancel</span>
                  </button>
                  <h2 className={`text-xl font-semibold flex-1 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Add Goal</h2>
                  <div className="w-12" />
                </div>
              </div>
            </div>
          </header>
        )}
        {/* Goal Types - styled as cards like QuranSettings */}
        <div className="space-y-4 pt-6 pb-2 px-2 flex-1 overflow-y-auto">
          {goalTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => setSelectedType(type)}
              className={`w-full text-left rounded-2xl shadow-sm transition-all duration-200 border flex flex-col items-start px-4 py-5 ${
                isDarkMode
                  ? 'bg-[#111111] border-gray-800 hover:bg-white/5'
                  : 'bg-white border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center w-full justify-between">
                <span className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{type.title}</span>
                <ChevronRight className={isDarkMode ? 'text-gray-600' : 'text-gray-400'} />
              </div>
              <span className={`block mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{goalTypeDescriptions[type.id]}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Sub-sheet for selecting an option for the selected goal type
  return (
    <SelectionView
      show={true}
      onClose={() => setSelectedType(null)}
      title={selectedType.title}
      options={selectedType.options.map(option => ({
        value: option.value,
        label: option.label,
        description: goalOptionDescriptions[option.value]
      }))}
      selectedValue={selectedOption}
      onSelect={setSelectedOption}
      isDarkMode={isDarkMode}
      // Add helper copy under the title
      preview={
        <div className={`mb-2 px-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{goalTypeDescriptions[selectedType.id]}</div>
      }
      className="pt-2"
      rightButton={selectedOption ? {
        text: 'Save',
        onClick: async () => {
          await handleGoalCreate();
          setSelectedType(null); // Close the sheet
        }
      } : undefined}
    />
  );
};

const GoalCard = ({ goal, isDarkMode, onMarkAction }) => {
  // Always call hooks first
  const todayProgress = useGoalProgress(goal, new Date());

  if (!goal || !goal.type) {
    return null;
  }

  const todayStr = new Date().toISOString().split('T')[0];
  // Get today's log from actionLogs
  const todayLog = goal.actionLogs ? goal.actionLogs[todayStr] : null;
  const progressPercentage = todayLog
    ? Math.min(100, (todayLog.completed / todayLog.expected) * 100)
    : 0;
  
  // Display text such as "Completed: 0 of 1"
  const getProgressDisplay = () => {
    if (!todayLog) return '';
    return `Completed: ${todayLog.completed} of ${todayLog.expected}`;
  };

  // Handler to mark today's action as done (incrementing "completed")
  const handleMarkToday = async () => {
    if (!todayLog || todayLog.completed >= todayLog.expected) return;
    const newCompleted = todayLog.completed + 1;
    const updatedLog = {
      ...todayLog,
      completed: newCompleted,
      timestamp: new Date().toISOString()
    };
    // Call the update function passed as a prop (expected to interface with your DB hook)
    await onMarkAction(goal.id, todayStr, updatedLog);
  };

  // Determine the next due step for this goal
  let nextStep = null;
  if (goal.type === 'quran_reading' && todayProgress && todayProgress.displayRange) {
    nextStep = `Read: ${todayProgress.displayRange}`;
  } else if (goal.type === 'memorization' && todayProgress && todayProgress.dailyTarget) {
    nextStep = `Memorize: ${todayProgress.dailyTarget}`;
  } else if (goal.type === 'dhikr' && todayProgress && todayProgress.dailyTarget) {
    nextStep = `Dhikr: ${todayProgress.dailyTarget}`;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`mb-4 px-0 rounded-[24px] border backdrop-blur-xl ${
        isDarkMode
          ? 'bg-white/5 border-neutral-800'
          : 'bg-white/60 border-gray-200'
      }`}
    >
      <div className="py-6 px-6">
        <div className="flex items-start justify-between">
          <div>
            <h3
              className={`text-lg font-medium mb-2 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              {goal.title}
            </h3>
            {nextStep && (
              <div className={`text-sm mb-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>{nextStep}</div>
            )}
            <p
              className={`text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}
            >
              {getProgressDisplay()}
            </p>
          </div>
        </div>

        {/* Progress section */}
        {/*
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                Today's Progress
              </span>
              <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
              {Math.round(progressPercentage)}%
              </span>
            </div>
          <div
            className={`h-2 rounded-full overflow-hidden ${
              isDarkMode ? 'bg-gray-500' : 'bg-gray-200'
            }`}
          >
              <div
                className={`h-full rounded-full ${
                  isDarkMode ? 'bg-emerald-500' : 'bg-emerald-600'
                }`}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          {todayLog && todayLog.completed < todayLog.expected && (
            <button
              onClick={handleMarkToday}
              className="mt-2 p-2 rounded bg-blue-500 text-white text-sm"
            >
              Mark Today's Action
            </button>
          )}
          </div>
        */}
      </div>
    </motion.div>
  );
};

export default function MobileGoalPlanner() {
  const { isDarkMode } = useDarkMode();
  const { goals, loading, deleteGoal, createGoal, updateGoalProgress, refresh } = useGoals();
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [showDeleteMode, setShowDeleteMode] = useState(false);
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [pendingGoalCreation, setPendingGoalCreation] = useState(null);
  const { currentUser } = useAuth();
  const { settings, updateSettings } = useSettings();

  // Add-goal overlay state
  const [selectedType, setSelectedType] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showOptionSubsheet, setShowOptionSubsheet] = useState(false);

  // Get progress for today only
  const allProgress = useMultiGoalProgress(goals, [new Date()]);
  const todayStr = format(new Date(), 'yyyy-MM-dd');

  console.log('Raw goals from useGoals:', goals);

  // Ensure goals is an array and filter out any invalid goals
  const activeGoals = Array.isArray(goals) 
    ? goals.filter(goal => {
        console.log('Checking goal:', goal);
        if (!goal) {
          console.log('Found null/undefined goal');
          return false;
        }
        if (typeof goal !== 'object') {
          console.log('Found non-object goal:', typeof goal);
          return false;
        }
        if (!goal.id) {
          console.log('Found goal without id:', goal);
          return false;
        }
        if (goal.status !== 'active') {
          console.log('Found non-active goal:', goal.status);
          return false;
        }
        return true;
      })
    : [];

  console.log('Filtered active goals:', activeGoals);

  const handleGoalCreated = async (type, title, recurrenceType, details) => {
    if (!type || !title) {
      console.log('Missing required goal fields:', { type, title });
      return;
    }
    
    try {
      // For Quran reading goals, check existing progress
      if (type === 'quran_reading') {
        const existingProgress = settings.quran?.readingProgress?.history || {};
        const hasProgress = Object.keys(existingProgress).length > 0;
        
        if (hasProgress) {
          // Store the goal details and show confirmation dialog
          setPendingGoalCreation({ type, title, recurrenceType, details });
          setShowProgressDialog(true);
          return;
        }
      }
      
      // ---------- New code for initializing action logs ----------
      const today = new Date();
      const startDateStr = today.toISOString().split('T')[0];
      // Use the provided duration or default to 30 days
      const duration = details.duration || 30;
      const endDate = addDays(today, duration - 1);
      const endDateStr = endDate.toISOString().split('T')[0];
      // Determine the expected daily action
      const expectedAction =
        details.dailyHizb ||
        details.dailyPages ||
        details.dailyVerses ||
        (type === 'dhikr' && details.requiredDhikrs
          ? details.requiredDhikrs.reduce((acc, d) => acc + d.count * d.repetitions, 0)
          : 1);
      const actionLogs = initializeActionLogsForGoal(startDateStr, endDateStr, expectedAction);
      const newDetails = { ...details, startDate: startDateStr, endDate: endDateStr, actionLogs };
      // -------------------------------------------------------------

      console.log('Creating new goal:', { type, title, recurrenceType, details: newDetails });
      const newGoal = await createGoal(type, title, recurrenceType, newDetails);
      console.log('Goal created successfully:', newGoal);
      setShowAddGoal(false);
    } catch (error) {
      console.error('Error creating goal:', error);
    }
  };

  const handleProgressConfirmed = async (includeProgress) => {
    if (!pendingGoalCreation) return;
    
    try {
      if (!includeProgress) {
        // Clear existing reading progress
        await updateSettings({
          ...settings,
          quran: {
            ...settings.quran,
            readingProgress: { history: {} },
            lastReadingSession: null
          }
        });
      }
      
      // Create the goal
      const { type, title, recurrenceType, details } = pendingGoalCreation;
      await createGoal(type, title, recurrenceType, details);
      
      // Reset state
      setPendingGoalCreation(null);
      setShowProgressDialog(false);
      setShowAddGoal(false);
    } catch (error) {
      console.error('Error handling progress confirmation:', error);
    }
  };

  // Multi-select delete logic
  const toggleGoalSelection = (goalId) => {
    setSelectedGoals((prev) =>
      prev.includes(goalId) ? prev.filter((id) => id !== goalId) : [...prev, goalId]
    );
  };

  // Batch delete for selected goals
  const deleteSelectedGoals = async () => {
    try {
      if (!currentUser) {
        // Guest: localStorage batch delete
        const LOCAL_STORAGE_KEY = 'ummahti_goals';
        const data = localStorage.getItem(LOCAL_STORAGE_KEY);
        const allGoals = data ? JSON.parse(data) : [];
        const updatedGoals = allGoals.filter(g => !selectedGoals.includes(g.id));
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedGoals));
      } else {
        // Logged-in: Firestore batch delete
        const updatedGoals = goals.filter(g => !selectedGoals.includes(g.id));
        const goalsRef = doc(db, 'userGoals', currentUser.uid, 'goals', 'active');
        await setDoc(goalsRef, { goals: updatedGoals });
      }
      setSelectedGoals([]);
      setShowDeleteMode(false);
      await refresh();
    } catch (error) {
      console.error('Error removing selected goals:', error);
    }
  };

  const renderGoalProgress = (goal) => {
    const dayProgress = allProgress.get(todayStr);
    if (!dayProgress) return null;

    const progress = dayProgress.get(goal.id);
    if (!progress) return null;

    return (
      <div className="flex flex-col gap-1">
        <div className="text-sm text-gray-500">
          {goal.type === 'quran_reading' 
            ? `${progress.dailyTarget} per day` 
            : progress.dailyTarget}
        </div>
        <div className="text-sm">
          Today's Progress: {progress.percentComplete}%
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress.percentComplete}%` }}
          />
        </div>
      </div>
    );
  };

  // Main add-goal sheet animation: slide left when option subsheet is open
  const addGoalSheetX = showOptionSubsheet ? '-15%' : '0%';
  const addGoalSheetPointerEvents = showOptionSubsheet ? 'none' : 'auto';

  // Helper for add-goal overlay header
  const AddGoalHeader = ({ onBack, title }) => (
    <header className="sticky top-0 z-40">
      <div className={`${isDarkMode ? 'bg-[#111111] border-white/5' : 'bg-white border-black/5'} backdrop-blur-xl border-b`}>
        <div style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' }} className="px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className={`flex items-center ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}
            >
              <ChevronRight className="w-5 h-5 rotate-180" />
              <span className="text-xl">Back</span>
            </button>
            <h2 className={`text-xl font-semibold flex-1 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h2>
            <div className="w-12" />
          </div>
        </div>
      </div>
    </header>
  );

  // Main add-goal sheet (goal type selection)
  const renderAddGoalSheet = () => (
    <motion.div
      initial={false}
      animate={{ x: addGoalSheetX }}
      transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
      className={`fixed inset-0 z-50 flex flex-col ${isDarkMode ? 'bg-[#111111]' : 'bg-white'}`}
      style={{ pointerEvents: addGoalSheetPointerEvents }}
    >
      <AddGoalHeader
        onBack={() => setShowAddGoal(false)}
        title="Add Goal"
      />
      <div className="flex-1 overflow-y-auto px-4 pb-24">
        <div className="space-y-4 pt-6 pb-2 flex-1 overflow-y-auto">
          {goalTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => {
                setSelectedType(type);
                setShowOptionSubsheet(true);
                setSelectedOption(null);
              }}
              className={`w-full text-left rounded-2xl shadow-sm transition-all duration-200 border flex flex-col items-start px-4 py-5 ${
                isDarkMode
                  ? 'bg-[#111111] border-gray-800 hover:bg-white/5'
                  : 'bg-white border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center w-full justify-between">
                <span className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{type.title}</span>
                <ChevronRight className={isDarkMode ? 'text-gray-600' : 'text-gray-400'} />
              </div>
              <span className={`block mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{goalTypeDescriptions[type.id]}</span>
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );

  // Option subsheet (goal option selection)
  const renderOptionSubsheet = () => (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
      className={`fixed inset-0 z-50 flex flex-col ${isDarkMode ? 'bg-[#111111]' : 'bg-white'}`}
    >
      <AddGoalHeader
        onBack={() => {
          setShowOptionSubsheet(false);
          setSelectedOption(null);
        }}
        title={selectedType?.title || ''}
      />
      <div className="flex-1 overflow-y-auto px-4 pb-24 pt-2">
        <div className={`mb-2 px-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{selectedType && goalTypeDescriptions[selectedType.id]}</div>
        {selectedType && selectedType.options.map(option => (
          <button
            key={option.value}
            className={`w-full px-4 py-3 text-left border-b ${
              isDarkMode ? 'border-gray-800' : 'border-gray-200'
            } last:border-0 ${selectedOption && selectedOption.value === option.value ? 'bg-emerald-50 dark:bg-emerald-900/10' : ''}`}
            onClick={() => setSelectedOption(option)}
          >
            <div className="flex items-center justify-between">
              <span className={`${
                selectedOption && selectedOption.value === option.value
                  ? 'text-emerald-500'
                  : isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>{option.label}</span>
              {selectedOption && selectedOption.value === option.value && (
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
              )}
            </div>
            {option.description && (
              <div className={`mt-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{goalOptionDescriptions[option.value]}</div>
            )}
          </button>
        ))}
        {selectedOption && (
          <button
            className={`mt-6 w-full py-3 rounded-xl font-semibold text-lg transition-colors ${
              isDarkMode
                ? 'bg-emerald-500 text-black hover:bg-emerald-400'
                : 'bg-emerald-600 text-white hover:bg-emerald-700'
            }`}
            onClick={async () => {
              await handleGoalCreated(
                selectedType.id,
                selectedOption.label,
                selectedOption.recurrenceType,
                selectedOption.details
              );
              setShowAddGoal(false);
              setShowOptionSubsheet(false);
              setSelectedType(null);
              setSelectedOption(null);
            }}
          >
            Save
          </button>
        )}
      </div>
    </motion.div>
  );

  return (
    <div className={`min-h-screen relative overflow-hidden ${
      isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-100'
    }`}>
      {/* Main sheet (always present) */}
      <motion.div
        initial={false}
        animate={{ x: showAddGoal ? (showOptionSubsheet ? '-15%' : '0%') : '0%' }}
        transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
        className="min-h-screen flex flex-col relative z-10"
        style={{ pointerEvents: showAddGoal && showOptionSubsheet ? 'none' : 'auto' }}
      >
        {/* Header */}
        <header className="sticky top-0 z-40">
          <div>
            <div style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' }} className="px-4 py-4">
              <div className="flex items-center justify-between">
                <h1 className={`text-2xl font-semibold ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Goals</h1>
                {activeGoals.length > 0 && !showDeleteMode && (
                  <button
                    onClick={() => setShowDeleteMode(true)}
                    className={`p-2 rounded-xl transition-colors ${
                      isDarkMode
                        ? 'text-red-400 hover:bg-red-500/10'
                        : 'text-red-600 hover:bg-red-50'
                    }`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
                {showDeleteMode && (
                  <button
                    onClick={() => {
                      setShowDeleteMode(false);
                      setSelectedGoals([]);
                    }}
                    className={`p-2 rounded-xl transition-colors ${
                      isDarkMode
                        ? 'text-gray-400 hover:bg-gray-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="px-4 relative z-10">
          <AnimatePresence mode="sync">
            {activeGoals.map(goal => {
              if (!goal || !goal.id) return null;
              return (
                <div key={goal.id} className="relative flex items-center">
                  {showDeleteMode && (
                    <input
                      type="checkbox"
                      checked={selectedGoals.includes(goal.id)}
                      onChange={() => toggleGoalSelection(goal.id)}
                      className="mr-4 w-5 h-5 accent-red-500 rounded-full border-2 border-gray-300 dark:border-neutral-700 focus:ring-2 focus:ring-red-400 transition-all duration-200 bg-white dark:bg-neutral-900"
                    />
                  )}
                  <div className="flex-1">
                    <GoalCard
                      goal={goal}
                      isDarkMode={isDarkMode}
                      onMarkAction={updateGoalProgress}
                    />
                  </div>
                </div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Empty State */}
        {activeGoals.length === 0 && !loading && (
          <div className="px-4 py-12 text-center relative">
            <div className={`relative backdrop-blur-xl rounded-2xl p-8 ${
              isDarkMode ? 'bg-white/[0.02]' : 'bg-white/[0.3]'
            }`}>
              <img src="/images/empty_states/goals_main_empty_state.png" alt="No goals" className="w-70 h-70 mx-auto mb-6" />
              <h3 className={`text-xl font-medium mb-3 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>Set Your First Goal</h3>
              <p className={`text-sm max-w-xs mx-auto ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Begin your journey by setting meaningful goals for Quran reading, memorization, or daily dhikr.
              </p>
            </div>
          </div>
        )}

        {/* Bottom Action Bar: Add Goal (right) */}
        <div
          className="fixed left-0 right-0 flex flex-row-reverse justify-between items-center px-6 z-50 md:hidden"
          style={{ bottom: 'calc(env(safe-area-inset-bottom) + 5.5rem)' }}
        >
          {!showDeleteMode && (
            <button
              onClick={() => setShowAddGoal(true)}
              className={`w-20 h-20 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 text-3xl font-bold
                ${isDarkMode
                  ? 'bg-emerald-500 text-black hover:bg-emerald-400'
                  : 'bg-gray-900 text-white hover:bg-gray-800'}
              `}
              style={{ fontSize: 32 }}
            >
              <Plus className="w-8 h-8" />
            </button>
          )}
          {/*
          <button
            onClick={clearAllProgress}
            className={`px-5 py-2 rounded-full text-sm font-semibold shadow transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2
              ${isDarkMode
                ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20'
                : 'bg-red-50 text-red-600 hover:bg-red-100'}
          `}
        >
          Clear Progress
        </button>
        */}
        </div>
      </motion.div>
      {/* Add-goal overlay */}
      <AnimatePresence>
        {showAddGoal && renderAddGoalSheet()}
      </AnimatePresence>
      {/* Option subsheet overlay */}
      <AnimatePresence>
        {showAddGoal && showOptionSubsheet && renderOptionSubsheet()}
      </AnimatePresence>
      {/* Multi-select Delete Popup */}
      {showDeleteMode && (
        <div className="fixed left-0 right-0 z-50 px-4 md:hidden" style={{ bottom: 'calc(env(safe-area-inset-bottom) + 6.5rem)' }}>
          <div className={`rounded-[24px] p-6 flex flex-col items-center border backdrop-blur-xl shadow-lg ${
            isDarkMode
              ? 'bg-white/5 border-neutral-800'
              : 'bg-white border-gray-200'
          }`}>
            <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Remove Selected Goals</h3>
            <p className={`text-sm mb-4 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {selectedGoals.length === 0
                ? 'Select the goals you want to remove.'
                : `You have selected ${selectedGoals.length} goal${selectedGoals.length > 1 ? 's' : ''} to remove. This action cannot be undone.`}
            </p>
            <div className="flex gap-3 w-full">
              <button
                onClick={() => {
                  setShowDeleteMode(false);
                  setSelectedGoals([]);
                }}
                className={`flex-1 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  isDarkMode
                    ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={deleteSelectedGoals}
                disabled={selectedGoals.length === 0}
                className={`flex-1 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  selectedGoals.length === 0
                    ? 'bg-red-200 text-white cursor-not-allowed'
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Progress Confirmation Dialog */}
      <AnimatePresence>
        {showProgressDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className={`w-full max-w-sm p-6 rounded-2xl ${
                isDarkMode ? 'bg-gray-900' : 'bg-white'
              }`}
            >
              <h3 className={`text-lg font-medium mb-2 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Existing Reading Progress
              </h3>
              <p className={`text-sm mb-6 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Are you sure you want to remove all goals? This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => handleProgressConfirmed(false)}
                  className={`flex-1 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                    isDarkMode
                      ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleProgressConfirmed(true)}
                  className="flex-1 px-4 py-2 rounded-xl text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
                >
                  Remove All
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
